import { deleteObject, listAll, ref, uploadBytes } from 'firebase/storage';
import { deleteField, serverTimestamp, updateDoc } from 'firebase/firestore';
import type { ItemImage } from '@easymoving/types';
import { firebaseStorage } from '../firebase/init';
import { compressImage } from '../images/compress';
import { getItem, updateItem } from './items';
import { itemDoc } from './paths';
import {
  forgetPrivateImage,
  imagesForStorage,
  privateImageUrl,
} from './privateImages';
import { assertUnderItemImageCap } from './imageLimits';

function uniqueImagePath(uid: string, itemId: string, n: number): string {
  return `users/${uid}/items/${itemId}/img-${n}-${String(Math.random()).slice(2, 8)}.jpg`;
}

/**
 * Append a photo to an item. The FIRST image goes to `original.jpg` so the
 * Cloud Function upgrades it (display/thumb); extra images use unique paths and
 * keep the client-compressed version.
 */
export async function addImage(uid: string, itemId: string, file: File): Promise<void> {
  const item = await getItem(uid, itemId);
  const images = item?.images ?? [];
  assertUnderItemImageCap(images.length); // per-item cap (defense-in-depth; UI guards too)
  const first = images.length === 0;
  const path = first ? primaryImagePath(uid, itemId) : uniqueImagePath(uid, itemId, images.length);
  const compressed = await compressImage(file);
  const storageRef = ref(firebaseStorage(), path);
  forgetPrivateImage(path);
  await uploadBytes(storageRef, compressed, {
    contentType: 'image/jpeg',
    cacheControl: 'private, max-age=0, no-store',
  });
  const img: ItemImage = {
    storagePath: path,
    revision: Date.now(),
    isPrimary: first,
    createdAt: Date.now(),
  };
  if (first) {
    await updateDoc(itemDoc(uid, itemId), {
      images: [img],
      primaryImageUrl: deleteField(),
      primaryThumbUrl: deleteField(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateItem(uid, itemId, { images: [...imagesForStorage(images), img] });
  }
}

/** Remove one image; promote the next to primary if needed. */
export async function removeImage(uid: string, itemId: string, storagePath: string): Promise<void> {
  const item = await getItem(uid, itemId);
  if (!item) return;
  const target = (item.images ?? []).find((i) => i.storagePath === storagePath);
  const remaining = (item.images ?? []).filter((i) => i.storagePath !== storagePath);

  const paths = new Set([
    storagePath,
    ...(target?.displayPath ? [target.displayPath] : []),
    ...(target?.thumbPath ? [target.thumbPath] : []),
    // Legacy fixed derivative paths from builds before private-path metadata.
    ...(storagePath.endsWith('original.jpg')
      ? ['display.jpg', 'thumb.jpg'].map((f) => storagePath.replace('original.jpg', f))
      : []),
  ]);
  await Promise.all(
    [...paths].map(async (path) => {
      forgetPrivateImage(path);
      await deleteObject(ref(firebaseStorage(), path)).catch(() => undefined);
    }),
  );

  const wasPrimary = target?.isPrimary || remaining.every((i) => !i.isPrimary);
  if (wasPrimary && remaining.length > 0) remaining[0]!.isPrimary = true;
  await updateDoc(itemDoc(uid, itemId), {
    images: imagesForStorage(remaining),
    primaryImageUrl: deleteField(),
    primaryThumbUrl: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** Mark one image as the primary (shown in the gallery). */
export async function setPrimaryImage(uid: string, itemId: string, storagePath: string): Promise<void> {
  const item = await getItem(uid, itemId);
  if (!item) return;
  const images = (item.images ?? []).map((i) => ({ ...i, isPrimary: i.storagePath === storagePath }));
  if (!images.some((i) => i.isPrimary)) return;
  await updateDoc(itemDoc(uid, itemId), {
    images: imagesForStorage(images),
    primaryImageUrl: deleteField(),
    primaryThumbUrl: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** Storage path of an item's primary original (the Cloud Function watches `original.*`). */
export function primaryImagePath(uid: string, itemId: string): string {
  return `users/${uid}/items/${itemId}/original.jpg`;
}

/**
 * Compress + upload the item's primary photo, then point the item doc at it
 * immediately (so the gallery shows the client-compressed image right away).
 * The Storage Cloud Function later adds private display/thumbnail paths.
 */
export async function uploadPrimaryImage(
  uid: string,
  itemId: string,
  file: File,
): Promise<string> {
  const compressed = await compressImage(file);
  const path = primaryImagePath(uid, itemId);
  const storageRef = ref(firebaseStorage(), path);
  for (const imagePath of [path, path.replace('original.jpg', 'display.jpg'), path.replace('original.jpg', 'thumb.jpg')]) {
    forgetPrivateImage(imagePath);
  }
  await uploadBytes(storageRef, compressed, {
    contentType: 'image/jpeg',
    cacheControl: 'private, max-age=0, no-store',
  });

  const image: ItemImage = {
    storagePath: path,
    revision: Date.now(),
    isPrimary: true,
    createdAt: Date.now(),
  };
  await updateDoc(itemDoc(uid, itemId), {
    images: [image],
    primaryImageUrl: deleteField(),
    primaryThumbUrl: deleteField(),
    updatedAt: serverTimestamp(),
  });
  return privateImageUrl(path, image.revision);
}

/** Best-effort delete of an item's stored image files (ignore missing). */
export async function deleteItemImages(uid: string, itemId: string): Promise<void> {
  const directory = ref(firebaseStorage(), `users/${uid}/items/${itemId}`);
  const files = await listAll(directory).catch(() => null);
  if (!files) return;
  await Promise.all(
    files.items.map(async (fileRef) => {
      forgetPrivateImage(fileRef.fullPath);
      await deleteObject(fileRef).catch(() => undefined);
    }),
  );
}

/** Remove the primary image references from the item doc + storage. */
export async function clearPrimaryImage(uid: string, itemId: string): Promise<void> {
  await updateDoc(itemDoc(uid, itemId), {
    images: [],
    primaryImageUrl: deleteField(),
    primaryThumbUrl: deleteField(),
    updatedAt: serverTimestamp(),
  });
  await deleteItemImages(uid, itemId);
}
